import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  inputStateForValue,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingPorkHerdDetails,
  useMutateFootprintingPorkHerdDetails,
  usePracticeManagementPorkParameters,
  useTranslation,
} from "utils";

import { HARD_INPUT_STATES } from "consts";

import { Card, Flex, Form, FormInstance, InputNumber, PageSpin, SanityCheck, Typography } from "components";

interface PorkHerdDetailsProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const PorkHerdDetailsForm = ({ primaryDataId, readOnly, farmId, year }: PorkHerdDetailsProps) => {
  const { t } = useTranslation("footprintingPork");
  const formRef = useRef<FormInstance>(null);

  const [averageHerdSize, setAvgHerdSize] = useState<number | null>(null);
  const [rearingPeriodDays, setRearingPeriodDays] = useState<number | null>(null);
  const [pigLossPct, setPigLossPct] = useState<number | null>(null);

  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);
  const herdDetailsQuery = useGetFootprintingPorkHerdDetails(primaryDataId ?? "", { enabled: !!primaryDataId });
  const { data: herdDetails, isSuccess } = herdDetailsQuery;
  const porkPracticeQuery = usePracticeManagementPorkParameters(carbonProgram?.country?.toLowerCase() as PracticeManagement.CountryCode);
  const isLoading = [herdDetailsQuery, porkPracticeQuery].some(queryIsLoading);

  const { mutate: mutateHerdDetails } = useMutateFootprintingPorkHerdDetails(
    farmId,
    year,
    { averageHerdSize, rearingPeriodDays, pigLossPct: pigLossPct },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateHerdDetails();
      }
    },
    [isLoading, mutateHerdDetails, readOnly]
  );

  useEffect(() => {
    if (herdDetails && isSuccess) {
      setAvgHerdSize(herdDetails.averageHerdSize ?? null);
      setRearingPeriodDays(herdDetails.rearingPeriodDays ?? null);
      setPigLossPct(herdDetails.pigLossPct ?? null);

      formRef.current?.setFieldsValue({
        averageHerdSize: herdDetails.averageHerdSize ?? null,
        rearingPeriodDays: herdDetails.rearingPeriodDays ?? null,
        pigLossPct: formatFractionInput(herdDetails.pigLossPct ?? null),
      });

      const fieldsToValidate = (["averageHerdSize", "rearingPeriodDays"] as const).some(
        (field) => herdDetails[field] !== null && herdDetails[field] !== undefined
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [herdDetails, isSuccess]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div data-testid="footprinting-pork-herd-details-form">
      <Form ref={formRef}>
        <Flex vertical gap={16}>
          <Card border className="padding-200">
            <Flex vertical gap={16}>
              <Flex vertical gap={8}>
                <Typography variant="h4">{t("porkHerdDetailsForm.averageHerdSizeQuestion")}</Typography>
                <Typography variant="paragraph-m" color="grey-600">
                  {t("porkHerdDetailsForm.averageHerdSizeQuestionDescription")}
                </Typography>
              </Flex>
              <InputNumber
                disabled={readOnly}
                name="averageHerdSize"
                dataTestId="footprinting-herd-size-input"
                label={t("porkHerdDetailsForm.question.averageHerdSizeInput")}
                suffix={t("porkHerdDetailsForm.question.averageHerdSizeSuffix")}
                value={averageHerdSize}
                initialValue={averageHerdSize}
                onChange={(v) => setAvgHerdSize(v)}
                validateFirst
                rules={[
                  { required: true, type: "number", message: t("porkHerdDetailsForm.error.validNumberHerdSize") },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                        return Promise.reject(new Error(t("porkHerdDetailsForm.error.validNumberHerdSize")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Flex>
          </Card>

          <Card border className="padding-200">
            <Flex vertical gap={16}>
              <Flex vertical gap={8}>
                <Typography variant="h4">{t("porkHerdDetailsForm.rearingPeriodDaysQuestion")}</Typography>
                <Typography variant="paragraph-m" color="grey-600">
                  {t("porkHerdDetailsForm.rearingPeriodDaysQuestionDescription")}
                </Typography>
              </Flex>
              <Form.Item
                validateStatus={
                  rearingPeriodDays
                    ? HARD_INPUT_STATES.includes(inputStateForValue(rearingPeriodDays, porkPracticeQuery.data?.rearingPeriodDays))
                      ? "error"
                      : "success"
                    : "success"
                }
              >
                <InputNumber
                  disabled={readOnly}
                  name="rearingPeriodDays"
                  dataTestId="footprinting-rearing-period-days-input"
                  label={t("porkHerdDetailsForm.question.rearingPeriodDaysInput")}
                  suffix={t("porkHerdDetailsForm.question.rearingPeriodDaysSuffix")}
                  value={rearingPeriodDays}
                  initialValue={rearingPeriodDays}
                  onChange={(v) => setRearingPeriodDays(v)}
                  validateFirst
                  guidanceText={porkPracticeQuery.data?.rearingPeriodDays}
                  rules={[
                    {
                      required: true,
                      type: "number",
                      message: t("porkHerdDetailsForm.error.validNumberDays", {
                        min: porkPracticeQuery.data?.rearingPeriodDays?.[1],
                        max: porkPracticeQuery.data?.rearingPeriodDays?.[2],
                      }),
                    },
                  ]}
                />
              </Form.Item>
              {rearingPeriodDays ? <SanityCheck inputValue={rearingPeriodDays} inputRange={porkPracticeQuery.data?.rearingPeriodDays} /> : null}
            </Flex>
          </Card>

          <Card border className="padding-200">
            <Flex vertical gap={16}>
              <Flex vertical gap={8}>
                <Typography variant="h4">{t("porkHerdDetailsForm.pigLossesQuestion")}</Typography>
                <Typography variant="paragraph-m" color="grey-600">
                  {t("porkHerdDetailsForm.pigLossesQuestionDescription")}
                </Typography>
              </Flex>
              <Form.Item
                validateStatus={
                  pigLossPct
                    ? HARD_INPUT_STATES.includes(inputStateForValue(pigLossPct, porkPracticeQuery.data?.pigLossesPct))
                      ? "error"
                      : "success"
                    : "success"
                }
              >
                <InputNumber
                  disabled={readOnly}
                  name="pigLossPct"
                  dataTestId="footprinting-pig-losses-input"
                  label={t("porkHerdDetailsForm.question.pigLossesInput")}
                  suffix="%"
                  value={formatFractionInput(pigLossPct ?? null)}
                  initialValue={formatFractionInput(pigLossPct ?? null)}
                  onChange={(v) => setPigLossPct(formatPercentageInput(v))}
                  precision={2}
                  validateFirst
                  guidanceText={porkPracticeQuery.data?.pigLossesPct}
                />
              </Form.Item>
              {pigLossPct ? <SanityCheck inputValue={pigLossPct} inputRange={porkPracticeQuery.data?.pigLossesPct} unit="%" /> : null}
            </Flex>
          </Card>
        </Flex>
      </Form>
    </div>
  );
};

PorkHerdDetailsForm.Key = "pork.herdDetails";
export { PorkHerdDetailsForm };
